import React, { useState } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import Brand from "@/components/Brand";
import { api } from "@/lib/api";
import { Store, User as UserIcon, Mail, Phone, FileText, Lock, ChevronRight } from "lucide-react";

const Field = ({ id, icon: Icon, label, type = "text", placeholder, value, onChange, hint }) => (
    <div>
        <label className="block text-xs uppercase tracking-wider text-elegant mb-2">{label}</label>
        <div className="relative">
            <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-elegant" />
            <input
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                data-testid={id}
                placeholder={placeholder}
                className="w-full h-12 pl-10 pr-4 bg-surface-elevated border border-[#1f2c44] rounded-md text-warm placeholder-[#5a6a85] focus:border-brand focus:outline-none transition-colors"
            />
        </div>
        {hint && <div className="text-[10px] text-elegant mt-1">{hint}</div>}
    </div>
);

const Registrazione = () => {
    const { user, setAuthFromResponse } = useAuth();
    const navigate = useNavigate();
    const [form, setForm] = useState({
        owner_name: "",
        restaurant_name: "",
        vat: "",
        phone: "",
        email: "",
        password: "",
        confirm: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");

    if (user) return <Navigate to="/" replace />;

    const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");

        // Client-side validation
        if (!form.owner_name.trim()) return setError("Inserisci nome e cognome");
        if (!form.restaurant_name.trim()) return setError("Inserisci il nome del locale");
        if (!form.vat.trim()) return setError("Inserisci la P.IVA");
        if (!form.email.trim()) return setError("Inserisci la tua email");
        if (form.password.length < 6) return setError("La password deve avere almeno 6 caratteri");
        if (form.password !== form.confirm) return setError("Le password non coincidono");

        setSubmitting(true);
        try {
            const { data } = await api.post("/auth/register", {
                owner_name: form.owner_name.trim(),
                restaurant_name: form.restaurant_name.trim(),
                vat: form.vat.trim(),
                phone: form.phone.trim(),
                email: form.email.trim().toLowerCase(),
                password: form.password,
            });
            // Auto-login: il backend già setta il cookie. Salvo anche in localStorage per consistency.
            if (data.token) {
                localStorage.setItem("access_token", data.token);
            }
            if (setAuthFromResponse) setAuthFromResponse(data);
            toast.success(`Benvenuto ${data.name}! Il tuo Smart Table è pronto.`);
            navigate("/");
            // Force reload to pick up the auth context
            setTimeout(() => window.location.assign("/"), 300);
        } catch (e) {
            setError(e?.response?.data?.detail || "Errore di registrazione");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-deep flex items-center justify-center p-6 py-10">
            <div className="w-full max-w-2xl bg-surface border border-[#1f2c44] rounded-lg shadow-2xl p-8 lg:p-10">
                <div className="mb-8 text-center">
                    <Brand size="md" />
                    <div className="text-xs uppercase tracking-[0.3em] text-brand mt-6 mb-2">Registra il tuo ristorante</div>
                    <h2 className="font-display text-3xl font-bold text-warm">Crea il tuo Smart Table</h2>
                    <p className="text-elegant text-sm mt-2">3 giorni di prova gratuita. Nessuna carta richiesta.</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5" data-testid="register-form">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Field id="register-owner-name" icon={UserIcon} label="Nome e cognome"
                            value={form.owner_name} onChange={(v) => setF("owner_name", v)}
                            placeholder="Mario Rossi" />
                        <Field id="register-restaurant-name" icon={Store} label="Nome del locale"
                            value={form.restaurant_name} onChange={(v) => setF("restaurant_name", v)}
                            placeholder="Trattoria Da Mario" />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Field id="register-vat" icon={FileText} label="Partita IVA"
                            value={form.vat} onChange={(v) => setF("vat", v)}
                            placeholder="IT12345678901"
                            hint="P.IVA italiana o estera" />
                        <Field id="register-phone" icon={Phone} label="Telefono" type="tel"
                            value={form.phone} onChange={(v) => setF("phone", v)}
                            placeholder="+39 333 1234567" />
                    </div>

                    <Field id="register-email" icon={Mail} label="Email amministratore" type="email"
                        value={form.email} onChange={(v) => setF("email", v)}
                        placeholder="amministratore@tuolocale.it"
                        hint="Userai questa email per accedere" />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Field id="register-password" icon={Lock} label="Password" type="password"
                            value={form.password} onChange={(v) => setF("password", v)}
                            placeholder="••••••••"
                            hint="Minimo 6 caratteri" />
                        <Field id="register-confirm" icon={Lock} label="Conferma password" type="password"
                            value={form.confirm} onChange={(v) => setF("confirm", v)}
                            placeholder="••••••••" />
                    </div>

                    {error && <div className="text-sm text-red-400" data-testid="register-error">{error}</div>}

                    <button
                        type="submit"
                        disabled={submitting}
                        data-testid="register-submit"
                        className="w-full h-13 py-3 bg-brand hover:bg-brand-light text-deep font-display font-bold uppercase tracking-[0.15em] rounded-md transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                        {submitting ? "Creazione in corso..." : <>Crea il mio Smart Table <ChevronRight className="w-5 h-5" /></>}
                    </button>

                    <p className="text-[10px] text-elegant text-center mt-3">
                        Registrandoti accetti i termini di utilizzo del servizio. Trial 3 giorni → poi piano Premium €49/mese.
                    </p>
                </form>

                <div className="mt-8 pt-6 border-t border-[#1f2c44] text-center">
                    <p className="text-elegant text-sm">
                        Hai già un account? <Link to="/login" data-testid="goto-login" className="text-brand hover:underline font-bold">Accedi</Link>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default Registrazione;
